import React, { useState, useEffect } from 'react';
import { Modal, Card, Button, Badge, Form, Alert, Tab, Tabs, Nav } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { API_ENDPOINTS } from '../config/api';
import UserAvatar from './UserAvatar';

const EventInvitationsModal = ({ show, onHide }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responding, setResponding] = useState(false);
  const [activeTab, setActiveTab] = useState('new');

  useEffect(() => {
    if (show) {
      fetchInvitations();
    }
  }, [show]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_ENDPOINTS.EVENT_INVITATIONS}/my-invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedInvitation) return;

    setResponding(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_ENDPOINTS.EVENT_INVITATIONS}/accept/${selectedInvitation.id}`, {
        message: responseMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowResponseModal(false);
      setSelectedInvitation(null);
      setResponseMessage('');
      fetchInvitations(); // Refresh the list
      
      // Show appropriate message based on response
      if (response.data.createdEvents && response.data.createdEvents.length > 1) {
        alert(`Invitation accepted! ${response.data.createdEvents.length} events have been added to your calendar.`);
      } else {
        alert('Invitation accepted! The event has been added to your calendar.');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedInvitation) return;

    setResponding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_ENDPOINTS.EVENT_INVITATIONS}/decline/${selectedInvitation.id}`, {
        message: responseMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowResponseModal(false);
      setSelectedInvitation(null);
      setResponseMessage('');
      fetchInvitations(); // Refresh the list
      alert('Invitation declined.');
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Failed to decline invitation. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const openResponseModal = (invitation, action) => {
    setSelectedInvitation({ ...invitation, action });
    setShowResponseModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'accepted':
        return <Badge bg="success">Accepted</Badge>;
      case 'declined':
        return <Badge bg="danger">Declined</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDateTime = (dateTime) => {
    return moment.utc(dateTime).local().format('MMM DD, YYYY h:mm A');
  };

  // Separate invitations into new (pending) and old (responded)
  const newInvitations = invitations.filter(inv => inv.status === 'pending');
  const oldInvitations = invitations.filter(inv => inv.status !== 'pending');

  const renderInvitationCard = (invitation) => (
    <div key={invitation.id} className="p-3 border-bottom custom-hover-bg-alt">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex flex-column">
        <h6 className="mb-0 fw-semibold">{invitation.event.title}</h6> 
        <div className="d-flex align-items-center text-muted small">
        <span>
          {formatDateTime(invitation.event.start)}
          {invitation.event.end && invitation.event.end !== invitation.event.start && (
            <span> - {formatDateTime(invitation.event.end)}</span>
          )}
        </span>
        {invitation.event.repeat && invitation.event.repeat !== 'none' && (
          <span className="text-info ms-2">
            <i className="bi bi-arrow-repeat me-1"></i>
            {invitation.event.repeat}
          </span>
        )}
      </div>
      </div>
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex flex-wrap gap-3 small text-muted">
            {invitation.event.location && (
              <span><i className="bi bi-geo-alt-fill text-danger me-1"></i>{invitation.event.location}</span>
            )}
            <div className="d-flex align-items-center gap-1"> <UserAvatar user={invitation.invitedByUser} size={24} alt="" /> <span className="fw-semibold">{invitation.invitedByUser.firstName} {invitation.invitedByUser.lastName}</span></div>
          </div>
          {getStatusBadge(invitation.status)}
          </div>
      </div>
      <div className="d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start gap-2">
     
      
      </div>
      <div className=" d-flex flex-row justify-content-between mt-2">
      <div className="d-flex flex-column w-100 gap-1">
      <div className="gap-1 rounded-2 bg-light p-2">{invitation.event.description && (
        <p className="mb-0"><span className="fw-semibold">Message:</span> {invitation.event.description}</p>
      )}
    </div>
      <div className="d-flex justify-content-between align-items-center">
        {invitation.status !== 'pending' && invitation.responseDate && (
          <span className="text-muted smaller">
            Responded {moment(invitation.responseDate).format('MMM DD, YYYY')}
          </span>
        )}  </div>

        
        {invitation.status === 'pending' && (
          <div className="d-flex gap-2">
            <Button
              variant="success"
              size="sm"
              onClick={() => openResponseModal(invitation, 'accept')}
            >
              <i className="bi bi-check me-1"></i>Accept
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => openResponseModal(invitation, 'decline')}
            >
              <i className="bi bi-x me-1"></i>Decline
            </Button>
          </div>
        )}
      </div> </div>
      
      {invitation.responseMessage && (
        <div className="mt-2 py-2 bg-light rounded-2">
          <small className="text-muted"><strong>Response:</strong> {invitation.responseMessage}</small>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton className="px-4 py-0">
          <div className="d-flex flex-row justify-content-start align-items-center w-100 gap-4">
            <h6 className={`mb-0 fw-semibold ${invitations.length > 0 ? '' : 'py-4'}`}>Event Invites</h6>
            {!loading && !error && invitations.length > 0 && (
              <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mt-2">
                <Nav.Item>
                  <Nav.Link eventKey="new">
                    New ({newInvitations.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="old">
                    Previous ({oldInvitations.length})
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading invitations...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="border-0 rounded-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          ) : invitations.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-calendar-x text-muted mb-3" style={{ fontSize: '48px' }}></i>
              <h6 className="text-muted">No Event Invitations</h6>
              <p className="text-muted small">You don't have any event invitations yet.</p>
            </div>
          ) : (
            <div className="max-height-500 overflow-auto">
              {activeTab === 'new' && (
                newInvitations.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bi bi-check-circle text-success mb-2" style={{ fontSize: '32px' }}></i>
                    <p className="text-muted small">All caught up! No pending invitations.</p>
                  </div>
                ) : (
                  <div>
                    {newInvitations.map(renderInvitationCard)}
                  </div>
                )
              )}
              {activeTab === 'old' && (
                oldInvitations.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bi bi-clock-history text-muted mb-2" style={{ fontSize: '32px' }}></i>
                    <p className="text-muted small">No previous invitations to show.</p>
                  </div>
                ) : (
                  <div>
                    {oldInvitations.map(renderInvitationCard)}
                  </div>
                )
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Response Modal */}
      <Modal show={showResponseModal} onHide={() => setShowResponseModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">
            {selectedInvitation?.action === 'accept' ? 'Accept' : 'Decline'} Invitation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvitation && (
            <div>
              <div className="bg-light rounded-3 p-3 mb-3">
                <h6 className="mb-2 fw-semibold">{selectedInvitation.event.title}</h6>
                <div className="d-flex align-items-center text-muted small mb-1">
                  <i className="bi bi-calendar me-2"></i>
                  {formatDateTime(selectedInvitation.event.start)}
                  {selectedInvitation.event.repeat && selectedInvitation.event.repeat !== 'none' && (
                    <span className="text-info ms-2">
                      <i className="bi bi-arrow-repeat me-1"></i>
                      {selectedInvitation.event.repeat}
                    </span>
                  )}
                </div>
                {selectedInvitation.event.location && (
                  <div className="d-flex align-items-center text-muted small">
                    <i className="bi bi-geo-alt me-2"></i>
                    {selectedInvitation.event.location}
                  </div>
                )}
              </div>
              
              <Form.Group>
                <Form.Label className="fw-semibold">Optional Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder={`Add a message to ${selectedInvitation.action === 'accept' ? 'accept' : 'decline'} this invitation...`}
                  className="border-0 bg-light"
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowResponseModal(false)} disabled={responding}>
            Cancel
          </Button>
          {selectedInvitation?.action === 'accept' ? (
            <Button variant="success" size="sm" onClick={handleAccept} disabled={responding} className="px-4">
              {responding ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Accepting...
                </>
              ) : (
                <>
                  <i className="bi bi-check me-2"></i>
                  Accept Invitation
                </>
              )}
            </Button>
          ) : (
            <Button variant="danger" size="sm" onClick={handleDecline} disabled={responding} className="px-4">
              {responding ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Declining...
                </>
              ) : (
                <>
                  <i className="bi bi-x me-2"></i>
                  Decline Invitation
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EventInvitationsModal; 